import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';
import { useGamificationContext } from '../../context/GamificationContext';

// components
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';

interface AccountBalance {
  balance: number;
  currency: string;
}

interface Transaction {
  id: number;
  transaction_type: string;
  stock_symbol: string | null;
  quantity: number;
  price_per_unit: number;
  total_amount: number;
  status: string;
  transaction_date: string;
  description?: string | null;
}

type OperationType = 'credit' | 'debit';

const WalletScreen: React.FC = () => {
  const { token } = useAuth();
  const { trackAction } = useGamificationContext();
  const [balance, setBalance] = useState<AccountBalance | null>(null);
  const [amount, setAmount] = useState<string>('');
  const [operationType, setOperationType] = useState<OperationType>('credit');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [transactions, setTransactions] = useState<Transaction[]>([]);

  useEffect(() => {
    fetchBalance();
    fetchTransactions();
  }, [token]);

  const fetchBalance = async () => {
    try {
      const response = await fetch(`${API_URL}/api/account/balance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setBalance(data);
      }
    } catch (err) {
      console.error('Error fetching balance:', err);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/transactions/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setTransactions(data);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Veuillez entrer un montant valide');
      return;
    }

    // For debit, check if sufficient balance
    if (operationType === 'debit' && balance && parsedAmount > balance.balance) {
      setError(`Solde insuffisant. Disponible: ${balance.balance.toLocaleString()} FCFA`);
      return;
    }

    setLoading(true);
    try {
      const endpoint = operationType === 'credit' ? '/api/account/credit' : '/api/account/debit';
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ amount: parsedAmount }),
      });

      if (response.ok) {
        const data = await response.json();
        setBalance(data);
        setAmount('');
        const actionText = operationType === 'credit' ? 'crédité' : 'débité';
        setSuccess(`Compte ${actionText} de ${parsedAmount.toLocaleString()} FCFA avec succès!`);
        fetchTransactions();
        if (operationType === 'credit') {
          trackAction('CREDIT_PORTFOLIO');
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || `Erreur lors du ${operationType}`);
      }
    } catch (err) {
      setError('Erreur réseau');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SiteLayout>
      <Header icon='account_balance_wallet' title='Ma Caisse (Cash)' />

      <div className='responsive-grid'>
        <div className='col-span-12'>
          <Box>
            <div className='box-title box-vertical-padding box-horizontal-padding'>
              <p>Solde du compte</p>
            </div>
            <div className='box-content box-content-height box-horizontal-padding'>
              {balance ? (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <h1 style={{ fontSize: '48px', margin: '20px 0', color: '#4CAF50' }}>
                    {balance.balance.toLocaleString()} {balance.currency}
                  </h1>
                  <p style={{ color: '#666' }}>Solde disponible</p>
                </div>
              ) : (
                <p>Chargement...</p>
              )}
            </div>
          </Box>

          <Box>
            <div className='box-title box-vertical-padding box-horizontal-padding'>
              <p>Opérations sur le compte</p>
            </div>
            <div className='box-content box-content-height box-horizontal-padding'>
              {/* Operation Type Tabs */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', marginTop: '10px' }}>
                <button
                  type='button'
                  onClick={() => { setOperationType('credit'); setError(''); setSuccess(''); }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    fontSize: '14px',
                    fontWeight: '600',
                    backgroundColor: operationType === 'credit' ? '#4CAF50' : 'var(--bg-input)',
                    color: operationType === 'credit' ? 'white' : 'var(--text-main)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  💰 Créditer
                </button>
                <button
                  type='button'
                  onClick={() => { setOperationType('debit'); setError(''); setSuccess(''); }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    fontSize: '14px',
                    fontWeight: '600',
                    backgroundColor: operationType === 'debit' ? '#ef4444' : 'var(--bg-input)',
                    color: operationType === 'debit' ? 'white' : 'var(--text-main)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  💸 Débiter
                </button>
              </div>

              <form onSubmit={handleSubmit} style={{ padding: '10px 0' }}>
                <div style={{ marginBottom: '20px' }}>
                  <label htmlFor='amount' style={{ display: 'block', marginBottom: '10px' }}>
                    Montant à {operationType === 'credit' ? 'créditer' : 'retirer'} (FCFA)
                  </label>
                  <input
                    type='number'
                    id='amount'
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder='Ex: 1000000'
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '16px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '4px',
                      background: 'var(--bg-input)',
                      color: 'var(--text-main)',
                    }}
                    disabled={loading}
                  />
                </div>

                {error && (
                  <div
                    style={{
                      padding: '10px',
                      marginBottom: '20px',
                      backgroundColor: '#ffebee',
                      color: '#c62828',
                      borderRadius: '4px',
                    }}
                  >
                    {error}
                  </div>
                )}

                {success && (
                  <div
                    style={{
                      padding: '10px',
                      marginBottom: '20px',
                      backgroundColor: '#e8f5e9',
                      color: '#2e7d32',
                      borderRadius: '4px',
                    }}
                  >
                    {success}
                  </div>
                )}

                <button
                  type='submit'
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '14px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    backgroundColor: loading ? '#ccc' : (operationType === 'credit' ? '#4CAF50' : '#ef4444'),
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {loading ? 'Traitement...' : (operationType === 'credit' ? 'Créditer mon compte' : 'Débiter mon compte')}
                </button>
              </form>
            </div>
          </Box>

          <Box>
            <div className='box-title box-vertical-padding box-horizontal-padding'>
              <p>Historique des opérations</p>
            </div>
            <div className='box-content box-horizontal-padding'>
              <div style={{ overflowX: 'auto', paddingBottom: '20px' }}>
                <table style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse', marginTop: '10px', minWidth: '600px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <th style={{ padding: '12px 8px', color: 'var(--text-main)' }}>Date</th>
                      <th style={{ padding: '12px 8px', color: 'var(--text-main)' }}>Type</th>
                      <th style={{ padding: '12px 8px', color: 'var(--text-main)' }}>Détails</th>
                      <th style={{ padding: '12px 8px', textAlign: 'right', color: 'var(--text-main)' }}>Montant (FCFA)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx) => (
                      <tr key={tx.id} style={{ borderBottom: '1px solid var(--border-color)', fontSize: '14px' }}>
                        <td style={{ padding: '12px 8px', color: 'var(--text-main)' }}>
                          {new Date(tx.transaction_date).toLocaleDateString('fr-FR', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontWeight: 'bold',
                            fontSize: '12px',
                            backgroundColor: tx.transaction_type === 'CREDIT' || tx.transaction_type === 'SELL' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                            color: tx.transaction_type === 'CREDIT' || tx.transaction_type === 'SELL' ? '#4CAF50' : '#ef4444'
                          }}>
                            {tx.transaction_type === 'CREDIT' ? 'DÉPÔT' :
                              tx.transaction_type === 'DEBIT' ? 'RETRAIT' :
                                tx.transaction_type === 'SPLIT' ? 'FRACTIONNEMENT' :
                                  tx.transaction_type === 'BUY' ? 'ACHAT' : 'VENTE'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 8px', color: 'var(--text-main)' }}>
                          {tx.description ? (
                            <span style={{ fontStyle: 'italic', opacity: 0.75 }}>{tx.description}</span>
                          ) : tx.transaction_type === 'CREDIT' ? 'Crédit du compte' :
                            tx.transaction_type === 'DEBIT' ? 'Débit du compte' :
                              tx.transaction_type === 'BUY' ? `Achat de ${tx.quantity} ${tx.stock_symbol}` :
                                `Vente de ${tx.quantity} ${tx.stock_symbol}`}
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 'bold', color: tx.transaction_type === 'CREDIT' || tx.transaction_type === 'SELL' ? '#4CAF50' : '#ef4444' }}>
                          {tx.transaction_type === 'SPLIT' ? '' : tx.transaction_type === 'CREDIT' || tx.transaction_type === 'SELL' ? '+' : '-'}
                          {tx.total_amount.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                    {transactions.length === 0 && (
                      <tr>
                        <td colSpan={4} style={{ padding: '20px', textAlign: 'center', color: 'var(--text-main)', opacity: 0.6 }}>
                          Aucune opération pour le moment
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </Box>
        </div>
      </div>
    </SiteLayout>
  );
};

export default WalletScreen;

